import React, { useMemo } from 'react';
import { Calendar, MapPin, Camera, Search, Settings, FileText, Copyright, GripVertical, ChevronDown, ChevronRight, Check, X, LockKeyhole } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Alert, AlertDescription, AlertTitle, Checkbox, Button } from '@exiftrator/shared/components/ui';
import { Card, CardHeader, CardContent, CardTitle } from '@exiftrator/shared/components/ui';
import { useFieldConfig } from '../../../contexts/FieldConfigContext';
import { useFeatureAccess } from '../../../contexts/LicenseContext';
import { useBasePath } from '../../../contexts/BasePathContext';
import { CATEGORY_METADATA } from '../../../config/fieldDefinitions';
import { cn } from '../../../components/utils/cn';
import { useNavigate } from 'react-router';

// Lucide icon component map
const ICON_MAP = {
  Calendar,
  MapPin,
  Camera,
  Search,
  Settings,
  FileText,
  Copyright
};

// Display group labels
const DISPLAY_GROUP_LABELS = {
  coordinates: 'Coordinates',
  camera: 'Camera Info',
  lens: 'Lens Info'
};

function SortableCategoryCard({ category, fields, config, onFieldToggle, onGroupToggle, onEnableAllInCategory, onDisableAllInCategory }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1
  };

  const enabledInCategory = fields.filter(f => config.enabledFields[f.id]).length;
  const allEnabled = enabledInCategory === fields.length;
  const IconComponent = ICON_MAP[category.icon];

  // Group fields by displayGroup
  const groupedFields = useMemo(() => {
    const groups = {};
    const ungrouped = [];

    fields.forEach(field => {
      if (field.displayGroup) {
        if (!groups[field.displayGroup]) {
          groups[field.displayGroup] = [];
        }
        groups[field.displayGroup].push(field);
      } else {
        ungrouped.push(field);
      }
    });

    return { groups, ungrouped };
  }, [fields]);

  // Check if all fields in a group are enabled
  const isGroupEnabled = (groupFields) => {
    return groupFields.every(f => config.enabledFields[f.id]);
  };

  // Check if any field in a group is enabled
  const isGroupPartiallyEnabled = (groupFields) => {
    return groupFields.some(f => config.enabledFields[f.id]);
  };

  const hasGroups = Object.keys(groupedFields.groups).length > 0;

  return (
    <Card ref={setNodeRef} style={style} className={cn(
      "transition-all",
      isDragging ? 'z-50 shadow-2xl scale-[1.02]' : ''
    )}>
      <CardHeader
        className="cursor-grab active:cursor-grabbing hover:bg-bg-elevated transition-colors select-none"
        {...attributes}
        {...listeners}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {IconComponent && <IconComponent size={18} className="text-text-primary" />}
            <div>
              <CardTitle className="text-[13px] font-bold text-text-primary">
                {category.label}
              </CardTitle>
              <div className="flex items-center gap-2">
                <p className="text-[10px] font-sans text-text-supporting">
                  {enabledInCategory} of {fields.length} enabled
                </p>
                {!allEnabled && enabledInCategory < fields.length && (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEnableAllInCategory(fields);
                      }}
                      className="h-auto p-0 text-[9px] font-bold text-accent-amber hover:text-accent-amber/80 hover:bg-transparent uppercase tracking-wider"
                      title="Enable all fields in this category"
                    >
                      <Check data-icon="inline-start" size={10} />
                      Enable All
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDisableAllInCategory(fields);
                      }}
                      className="h-auto p-0 text-[9px] font-bold text-accent-red hover:text-accent-red/80 hover:bg-transparent uppercase tracking-wider"
                      title="Disable all fields in this category"
                    >
                      <X data-icon="inline-start" size={10} />
                      Disable All
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          <GripVertical className="text-text-supporting opacity-30" size={16} />
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {hasGroups ? (
          <div className="space-y-4">
            {/* Render grouped fields as expandable sections */}
            {Object.entries(groupedFields.groups).map(([groupName, groupFields]) => {
              const allEnabled = isGroupEnabled(groupFields);
              const partiallyEnabled = !allEnabled && isGroupPartiallyEnabled(groupFields);

              return (
                <GroupedFieldSection
                  key={groupName}
                  groupName={groupName}
                  groupLabel={DISPLAY_GROUP_LABELS[groupName] || groupName}
                  fields={groupFields}
                  allEnabled={allEnabled}
                  partiallyEnabled={partiallyEnabled}
                  onToggle={() => onGroupToggle(groupFields, !allEnabled)}
                />
              );
            })}
            {/* Render ungrouped fields individually */}
            {groupedFields.ungrouped.length > 0 && (
              <div className="pt-1 border-t border-border-default/70">
                <div className="grid grid-cols-1 gap-y-1">
                  {groupedFields.ungrouped.map(field => (
                    <FieldToggle
                      key={field.id}
                      field={field}
                      enabled={config.enabledFields[field.id]}
                      onEnabledChange={() => onFieldToggle(field.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-y-1">
            {fields.map(field => (
              <FieldToggle
                key={field.id}
                field={field}
                enabled={config.enabledFields[field.id]}
                onEnabledChange={() => onFieldToggle(field.id)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Grouped field section component (no longer collapsible, checkbox moved to left)
function GroupedFieldSection({
  groupName,
  groupLabel,
  fields,
  allEnabled,
  partiallyEnabled,
  onToggle
}) {
  return (
    <div className="space-y-1">
      {/* Group header - checkbox moved to left, no collapse button */}
      <div className="flex items-center p-2 rounded-md bg-bg-hover">
        <div className="flex items-center gap-2 flex-1">
          {/* Checkbox now on the left */}
          <Checkbox
            checked={allEnabled}
            className="w-4 h-4"
            onCheckedChange={onToggle}
          />
          <span className="text-[10px] font-bold text-text-supporting uppercase tracking-wider">
            {groupLabel}
          </span>
          <span className="text-[10px] text-text-supporting">
            ({fields.map(f => f.label).join(', ')})
          </span>
        </div>
      </div>

      {/* Always show fields (no longer collapsible) */}
      <div className="pl-10 pr-2 py-1 space-y-1">
        {fields.map(field => (
          <div
            key={field.id}
            className={cn(
              "text-[10px] px-2 py-1 rounded transition-colors",
              allEnabled ? "text-text-primary" : "text-text-supporting"
            )}
          >
            {field.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function FieldToggle({ field, enabled, onEnabledChange }) {
  return (
    <label className="flex items-center justify-between gap-2 p-2 rounded-md hover:bg-bg-hover transition-colors cursor-pointer group">
      <div className="flex items-center gap-2 flex-1">
        <Checkbox
          checked={enabled}
          onCheckedChange={onEnabledChange}
          className="w-3.5 h-3.5"
        />
        <span className={cn(
          "text-[11px] transition-colors",
          enabled ? 'text-text-primary' : 'text-text-supporting'
        )}>
          {field.label}
        </span>
      </div>
    </label>
  );
}

function FieldSettings() {
  const navigate = useNavigate();
  const { appPath } = useBasePath();
  const fieldAccess = useFeatureAccess("fieldCustomization");
  const {
    config,
    updateFieldEnabled,
    updateCategoryOrder,
    resetToDefaults,
    getFieldsByCategory,
    enabledFieldCount
  } = useFieldConfig();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = config.categoryOrder.indexOf(active.id);
      const newIndex = config.categoryOrder.indexOf(over.id);
      const newOrder = arrayMove(config.categoryOrder, oldIndex, newIndex);
      updateCategoryOrder(newOrder);
    }
  };

  const handleFieldToggle = (fieldId) => {
    updateFieldEnabled(fieldId, !config.enabledFields[fieldId]);
  };

  // Toggle all fields in a group
  const handleGroupToggle = (groupFields, enable) => {
    // Count currently enabled fields to prevent disabling all
    if (!enable) {
      const enabledInGroup = groupFields.filter(f => config.enabledFields[f.id]).length;
      const totalEnabled = Object.values(config.enabledFields).filter(Boolean).length;
      if (totalEnabled - enabledInGroup < 1) {
        alert('At least one field must be enabled');
        return;
      }
    }

    groupFields.forEach(field => {
      updateFieldEnabled(field.id, enable);
    });
  };

  // Enable all fields in a category
  const handleEnableAllInCategory = (categoryFields) => {
    categoryFields.forEach(field => {
      updateFieldEnabled(field.id, true);
    });
  };

  // Disable all fields in a category (with safety check)
  const handleDisableAllInCategory = (categoryFields) => {
    // Count total enabled fields across all categories
    const totalEnabled = Object.values(config.enabledFields).filter(Boolean).length;
    const enabledInCategory = categoryFields.filter(f => config.enabledFields[f.id]).length;

    // Make sure we're not disabling the last field
    if (totalEnabled - enabledInCategory < 1) {
      alert('At least one field must be enabled');
      return;
    }

    categoryFields.forEach(field => {
      updateFieldEnabled(field.id, false);
    });
  };

  const handleReset = () => {
    if (confirm('Reset to default configuration? This will enable only the 7 basic fields and restore default category order.')) {
      resetToDefaults();
    }
  };

  return (
    <div className="flex flex-col h-full animate-in fade-in slide-in-from-bottom-2 duration-300">
      {!fieldAccess.allowed && (
        <Alert className="mb-4 border-accent-primary/30 bg-accent-primary/10">
          <LockKeyhole className="size-4 text-accent-primary" />
          <AlertTitle>Field customization is a Pro feature</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>Unlock Pro to reorder categories and control visible EXIF fields.</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(appPath("/settings/license"))}
            >
              Unlock Pro
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className={!fieldAccess.allowed ? "pointer-events-none opacity-60 select-none" : ""}>
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-sans text-[24px] font-bold text-text-primary">Exif Fields</h2>
        <div className="text-[12px] font-sans text-text-supporting bg-bg-hover px-4 py-2 rounded-md border border-border-default/70">
          {enabledFieldCount} fields enabled
        </div>
      </div>

      <div className="space-y-6 flex-1 overflow-y-auto pr-4 scrollbar-thin scrollbar-thumb-border-control/50 pb-8">
        <Card className="bg-bg-tertiary/50 border-border-default/70 shadow-inner">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-8">
              <p className="text-[12px] text-text-secondary leading-relaxed max-w-[500px]">
                Enable or disable metadata fields and drag categories to reorder them.
                Grouped fields (Coordinates, Camera Info, Lens Info) are enabled together.
              </p>
              <Button
                variant="outline"
                className="shrink-0 h-8 px-4 bg-accent-red/10 text-accent-red border border-accent-red/30 rounded-sm text-[11px] font-bold uppercase tracking-wider hover:bg-accent-red hover:text-bg-deep hover:border-accent-red transition-all shadow-sm"
                onClick={handleReset}
              >
                Reset Defaults
              </Button>
            </div>
          </CardContent>
        </Card>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={config.categoryOrder}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {config.categoryOrder.map((categoryId) => {
                const category = CATEGORY_METADATA[categoryId];
                const fields = getFieldsByCategory(categoryId, false);

                return (
                  <SortableCategoryCard
                    key={categoryId}
                    category={category}
                    fields={fields}
                    config={config}
                    onFieldToggle={handleFieldToggle}
                    onGroupToggle={handleGroupToggle}
                    onEnableAllInCategory={handleEnableAllInCategory}
                    onDisableAllInCategory={handleDisableAllInCategory}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      </div>
      </div>
    </div>
  );
}

export default FieldSettings;
